import React, { memo, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Button, Card, Radio, Spin, Typography, Tag, Modal, Flex, Space } from "antd"
import { useTranslation } from "react-i18next"
import { brandImage } from "components/bookings/helpers"
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import DeleteCardModal from "./deleteModal"
import PaymentMethode from "./PaymentMethode"
import { useDispatch, useSelector } from "react-redux"
import { detachPaymentMethod, getPaymentMethods, paymentMethodsSelector, updatePaymentMethod } from "store/user"
import styles from "./index.module.css"
import IconWithBg from "ui/IconWithBg"
import { useDevice } from "hooks/useDevice"
import CustomDrawer from "ui/Drawer"
import { toaster } from "components/common/Toast"

const { Text } = Typography

const PaymentMethodList = ({ active, onChange: changeHandler, client = {}, className, loading, onRefresh }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const paymentMethods = useSelector(paymentMethodsSelector)
  const hasPaymentMethods = !!paymentMethods.length
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteCardId, setDeleteCardId] = useState(null)
  const [deleteCardLast4, setDeleteCardLast4] = useState("")
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editCard, setEditCard] = useState(null)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null);


  const { isMobile } = useDevice()
  const openDeleteModal = (card) => {
    setDeleteCardId(card.id)
    setDeleteCardLast4(card.last4)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = () => {
    dispatch(detachPaymentMethod(deleteCardId)).then((response) => {
      setDeleteModalOpen(false)
      setDeleteCardId(null)
      setDeleteCardLast4("")
      toaster.error(response?.message)
    });

    if (client.id) {
      dispatch(getPaymentMethods(client.id))
    }
    if (onRefresh) onRefresh();
  }
  useEffect(() => {
    if (!selectedId && paymentMethods?.length) {
      const defaultCard = paymentMethods.find((c) => c.is_default);
      setSelectedId(defaultCard ? defaultCard.id : paymentMethods[0].id);
      if (defaultCard) changeHandler(defaultCard.id);
    }
  }, [paymentMethods, selectedId, changeHandler]);

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false)
    setDeleteCardId(null)
    setDeleteCardLast4("")
  }

  const handleEditChange = () => {
    setEditModalOpen(false)
    setEditCard(null)
    if (client.id) {
      dispatch(getPaymentMethods(client.id))
    }
    if (onRefresh) onRefresh()
  }

  const handleAddChange = () => {
    setAddModalOpen(false)
    if (client.id) {
      dispatch(getPaymentMethods(client.id))
    }
    if (onRefresh) onRefresh()
  }

  useEffect(() => {
    if (client.id) dispatch(getPaymentMethods(client.id))
  }, [client.id, dispatch])

  if (loading) {
    return <Spin style={{ width: '100%', margin: '40px 0' }} />
  }



  const handleSelect = (id) => {
    const selectedCard = paymentMethods.find((card) => card.id === id);
    if (selectedCard) {
      setSelectedId(selectedCard.payment_method_id);
    }
  };

  const saveChanges = async () => {
    const payload = {
      payment_method_id: selectedId,
      is_default: true
    }
    await dispatch(updatePaymentMethod(payload)).then((response) => {
      toaster.success(response?.message)
    })
    if (client.id) {
      dispatch(getPaymentMethods(client.id))
    }
    if (onRefresh) onRefresh()
  }

  return (
    <>
      <Flex vertical gap={8} className={`${className || ""}`}>
        {hasPaymentMethods &&
          paymentMethods.map((card) => (
            <Card key={card.id} className={`rounded-lg ${styles.cardListItem} `} bodyStyle={{}}>
              <Flex justify="space-between" align="center" className={`${styles.showOnMobile} mb-s`}>
                <Space size={8} align="center">
                  <Text strong>{card.brand.toUpperCase()}</Text>
                  {card.is_default && <Tag color="blue">{t("payment.default")}</Tag>}
                </Space>
                <Radio checked={active === card.id} onChange={() => changeHandler(card.id)} value={card.id} />
              </Flex>
              <Flex justify="space-between" align="center">
                <Flex align="center" gap={12} className="flex-1">
                  <Radio
                    className={styles.hideOnMobile}
                    checked={active === card.id}
                    onChange={() => changeHandler(card.id)}
                    value={card.id}
                    onClick={() => handleSelect(card.id)}
                  />
                  <img src={brandImage(card.brand)} alt={card.brand} style={{ width: 40, height: "auto" }} />
                  <Flex gap={10}>
                    <Text strong className={`${styles.hideOnMobile} pr-xs`}>
                      {card.brand.toUpperCase()}
                    </Text>
                    <Text strong>****{card.last4}</Text>
                    <Text type="secondary">
                      {t("payment.expires")} {String(card.exp_month).padStart(2, "0")}/{card.exp_year}
                    </Text>
                  </Flex>
                </Flex>
                <Flex gap={12} align="center">
                  {card.is_default && (
                    <Tag color="var(--color-primary-surface)" style={{ color: "var(--color-primary)" }} className={styles.hideOnMobile}>
                      {t("payment.default")}
                    </Tag>
                  )}
                  <IconWithBg
                    onClick={() => {
                      setEditCard(card)
                      setEditModalOpen(true)
                    }}
                    icon={<EditOutlined className={styles.cardActionIcons} />}
                  />

                  <IconWithBg icon={<DeleteOutlined className={styles.cardActionIcons} />} onClick={() => openDeleteModal(card)} />
                </Flex>
              </Flex>
            </Card>
          ))}

        <Button className="w-max mt-xs" type="link" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
          {t("payment.add_card")}
        </Button>

        <Flex gap={12} className="mt-s">
          <Button size="large" onClick={() => console.log("Cancel")}>
            {t("common.cancel")}
          </Button>
          <Button size="large" type="primary" onClick={saveChanges} disabled={active === selectedId}>
            {t("payment.save_changes")}
          </Button>
        </Flex>
      </Flex>

      <DeleteCardModal open={deleteModalOpen} onCancel={handleDeleteCancel} onConfirm={handleDeleteConfirm} cardLast4={deleteCardLast4} />
      {isMobile ? (
        <CustomDrawer
          open={editModalOpen || addModalOpen}
          setOpen={() => {
            setEditModalOpen(false)
            setAddModalOpen(false)
            setEditCard(null)
          }}
          title={editModalOpen ? t("payment.edit_card") : t("payment.add_card")}
          onCancel={() => {
            setEditModalOpen(false)
            setAddModalOpen(false)
            setEditCard(null)
          }}
          width={800}
          destroyOnClose
        >
          <PaymentMethode
            active={
              editModalOpen
                ? {
                  ...editCard,
                  billing_details: {
                    first_name: editCard?.first_name || "",
                    last_name: editCard?.last_name || "",
                    address: {
                      line1: editCard?.address_line_1 || "",
                      line2: editCard?.address_line_2 || "",
                      city: editCard?.city || "",
                      state: editCard?.state || "",
                      postal_code: editCard?.zip || "",
                      country: editCard?.country || ""
                    }
                  },
                  is_default: editCard?.is_default || false
                }
                : undefined
            }
            onCancel={() => {
              setEditModalOpen(false)
              setAddModalOpen(false)
              setEditCard(null)
            }}
            onChange={editModalOpen ? handleEditChange : handleAddChange}
            create={!editModalOpen}
          />
        </CustomDrawer>
      ) : (
        <Modal
          open={editModalOpen || addModalOpen}
          onCancel={() => {
            setEditModalOpen(false)
            setAddModalOpen(false)
            setEditCard(null)
          }}
          width={800}
          footer={null}
          title={editModalOpen ? t("payment.edit_card") : t("payment.add_card")}
          destroyOnClose
          className={styles.paymentMethodModal}
        >
          <PaymentMethode
            active={
              editModalOpen
                ? {
                  ...editCard,
                  billing_details: {
                    first_name: editCard?.first_name || "",
                    last_name: editCard?.last_name || "",
                    address: {
                      line1: editCard?.address_line_1 || "",
                      line2: editCard?.address_line_2 || "",
                      city: editCard?.city || "",
                      state: editCard?.state || "",
                      postal_code: editCard?.zip || "",
                      country: editCard?.country || ""
                    }
                  },
                  is_default: editCard?.is_default || false
                }
                : undefined
            }
            onCancel={() => {
              setEditModalOpen(false)
              setAddModalOpen(false)
              setEditCard(null)
            }}
            onChange={editModalOpen ? handleEditChange : handleAddChange}
            create={!editModalOpen}
          />
        </Modal>
      )}
    </>
  )
}

PaymentMethodList.propTypes = {
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onRefresh: PropTypes.func,
  client: PropTypes.shape({
    default_payment_method_id: PropTypes.string,
    id: PropTypes.string
  }),
  className: PropTypes.string,
  loading: PropTypes.bool,
}

export default memo(PaymentMethodList)
